// StatusConfig.tsx
import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Checkbox } from '@/components/common/Checkbox';
import { Edit2, Trash2 } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/common/Button';

interface Status {
  _id: string;
  name: string;
  color: string;
  followUpRequired: boolean;
  createdAt: string;
}

// Dummy data
const dummyStatuses: Status[] = [
  {
    _id: '1',
    name: 'To Do',
    color: '#3B82F6',
    followUpRequired: false,
    createdAt: '2025-01-12T00:00:00Z'
  },
  {
    _id: '2',
    name: 'In Process',
    color: '#F59E0B',
    followUpRequired: true,
    createdAt: '2025-01-12T00:00:00Z'
  },
  {
    _id: '3',
    name: 'In Review',
    color: '#8B5CF6',
    followUpRequired: true,
    createdAt: '2025-05-03T00:00:00Z'
  },
  {
    _id: '4',
    name: 'Help Required',
    color: '#06B6D4',
    followUpRequired: true,
    createdAt: '2025-05-03T00:00:00Z'
  },
  {
    _id: '5',
    name: 'Completed',
    color: '#10B981',
    followUpRequired: false,
    createdAt: '2025-05-03T00:00:00Z'
  },
  {
    _id: '6',
    name: 'Archived',
    color: '#6B7280',
    followUpRequired: false,
    createdAt: '2025-05-03T00:00:00Z'
  }
];

export const StatusConfig: React.FC<{ onSetActionCallback?: (callbacks: { add?: () => void }) => void }> = ({ onSetActionCallback }) => {
  const [statuses, setStatuses] = useState<Status[]>(dummyStatuses);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    color: '#3b82f6',
    followUpRequired: false 
  });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    onSetActionCallback?.({
      add: () => setShowAddModal(true)
    });
  }, [onSetActionCallback]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedStatuses(statuses.map(status => status._id));
    } else {
      setSelectedStatuses([]);
    }
  };

  const handleSelectStatus = (statusId: string) => {
    setSelectedStatuses(prev => 
      prev.includes(statusId) 
        ? prev.filter(id => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handleSubmit = async () => {
    try {
      if (editingStatus) {
        // Update existing status
        const updatedStatuses = statuses.map(status =>
          status._id === editingStatus._id
            ? { ...status, ...formData }
            : status
        );
        setStatuses(updatedStatuses);
      } else {
        // Create new status
        const newStatus: Status = {
          _id: Date.now().toString(),
          ...formData,
          createdAt: new Date().toISOString()
        };
        setStatuses([...statuses, newStatus]);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving status:', error);
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setEditingStatus(null);
    setFormData({ name: '', color: '#3b82f6', followUpRequired: false });
  };

  const handleEdit = (status: Status) => {
    setEditingStatus(status);
    setFormData({ 
      name: status.name, 
      color: status.color,
      followUpRequired: status.followUpRequired || false 
    });
    setShowAddModal(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}" status?`)) {
      setStatuses(statuses.filter(status => status._id !== id));
      setSelectedStatuses(selectedStatuses.filter(statusId => statusId !== id));
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border border-gray-200 border-collapse">
            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-[80%]">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 mr-3"
                      onChange={handleSelectAll}
                      checked={selectedStatuses.length === statuses.length && statuses.length > 0}
                    />
                    <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                      STATUS
                    </span>
                  </div>
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 w-[15%] text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ADDED ON
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 w-[5%] text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-gray-500 border">
                    Loading statuses...
                  </td>
                </tr>
              ) : statuses.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-gray-500 border">
                    No statuses found
                  </td>
                </tr>
              ) : (
                statuses.map((status) => (
                  <tr key={status._id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1 w-[80%]">
                      <div className="flex items-center">
                        <input 
                          type="checkbox" 
                          className="rounded border-gray-300 mr-3"
                          checked={selectedStatuses.includes(status._id)}
                          onChange={() => handleSelectStatus(status._id)}
                        />
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{ backgroundColor: status.color }}
                        />
                        <span className="text-sm font-medium">{status.name}</span>
                      </div>
                    </td>
                    <td className="px-4 border py-1 w-[15%] text-sm text-gray-600">
                      {formatDate(status.createdAt)}
                    </td>
                    <td className="px-4 border py-1 w-[5%]">
                      <div className="flex items-center justify-end space-x-1">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          onClick={() => handleEdit(status)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDelete(status._id, status.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{statuses.length}</span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={handleClose}
        title={editingStatus ? 'Edit Status' : 'Add Status'}
        size="md"
      >
        <div className="space-y-4 px-4">
          <Input
            label="Status Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter status name"
            required
          />

          <div>
            <Checkbox
              label="Follow Up Required"
              checked={formData.followUpRequired}
              onChange={(e) => setFormData({ ...formData, followUpRequired: e.target.checked })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="space-y-3">
              <HexColorPicker
                color={formData.color}
                onChange={(color) => setFormData({ ...formData, color })}
                style={{ width: '100%', height: '200px' }}
              />
              <div className="flex items-center space-x-2">
                <div
                  className="w-10 h-10 rounded border border-gray-300"
                  style={{ backgroundColor: formData.color }}
                />
                <span className="text-sm text-gray-600 font-mono">{formData.color}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formData.name.trim()}
            >
              {editingStatus ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};